import React, {
  useRef,
  useState,
} from 'react';
import {
  useStripe,
} from '@stripe/react-stripe-js';

import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  Button,
  AddCardForm,
} from 'web-components';

import {
  Flex,
  Text,
} from 'web-styled';

import * as S from '../styled';

const mapState = state => ({
  defaultCardId: coreSelectors.defaultCardIdSelector(state),
  paymentMethods: coreSelectors.getResourceMappedList(
    'paymentMethods',
  )(state),
});

type Props = {
  planId?: number,
  disabled?: boolean,
  isUpgrade?: boolean,
  clientSecret: string,
  subscriptionId: string,
  onError?: { (error: any): void },
  onSuccess?: { (intent: any): void },
};

const Buy = ({
  planId,
  disabled = false,
  isUpgrade = false,
  subscriptionId,
  clientSecret,
  onError,
  onSuccess,
}: Props) => {
  const stripe = useStripe();
  const {
    defaultCardId,
    paymentMethods,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();
  const [errorMsg, setError] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [cardFormIsVisible, setCardFormVisible] = useState(false);
  const isSubmittingRef = useRef(false);

  const onSetDefaultPaymentMethod = (method: any) => {
    dispatch(
      coreActions.requestSetDefaultPaymentMethod({
        id: method.id,
      }),
    );
  };

  const handleAddCard = (res, actions) => {
    dispatch(
      coreActions.requestCreatePaymentMethod({
        body: {
          id: res.paymentMethod.id,
        },
        callbacks: {
          ...actions,
          onSuccess: () => {
            setCardFormVisible(false);
          },
        },
      }),
    );
  };

  const onUpgrade = () => {
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setError(null);
    setLoading(true);

    dispatch(
      coreActions.requestUpgradeAccount({
        endpoint: 'upgrade',
        callbacks: {
          onError: () => {
            isSubmittingRef.current = false;
            setLoading(false);
          },
          onSuccess: (r) => {
            isSubmittingRef.current = false;
            setLoading(false);
            onSuccess?.(r);
          },
        },
      }),
    );
  };

  const onSubscribeProduct = () => {
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setError(null);
    setLoading(true);
    const setPlanSuccess = (response) => {
      dispatch(
        coreActions.requestFetchClientEntity({
          list: 'library',
          resourceType: 'personalWorkouts',
        }),
      );
      dispatch(
        coreActions.requestFetchClientEntity({
          list: 'library',
          resourceType: 'personalPrograms',
        }),
      );
      isSubmittingRef.current = false;
      setLoading(false);
      onSuccess?.(response);
    };

    const action = subscriptionId
      ? coreActions.requestUpdateSubscription
      : coreActions.requestSetPlan;

    dispatch(
      action({
        id: subscriptionId,
        isClient: true,
        body: subscriptionId ? {
          plan_id: planId,
        } : {
          product_plan_id: planId,
        },
        callbacks: {
          onError: (e) => {
            isSubmittingRef.current = false;
            onError?.(e);
            setLoading(false);
          },
          onSuccess: setPlanSuccess,
        },
      }),
    );
  };

  const onBuy = async () => {
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setError(null);
    setLoading(true);
    const method = paymentMethods.find(
      m => m.id === defaultCardId,
    ) || paymentMethods[0];
    try {
      const {
        error,
        paymentIntent,
      } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: method?.id,
        },
      );

      if (error) {
        setError(error.message);
        onError?.(error);
        console.log('error', error);
      } else {
        onSuccess?.(paymentIntent);
      }
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  const buttonText = planId ? 'Subscribe' : 'Buy Now';

  return (
    <>
      <Flex width="100%" column>
        <Text>
          Pay with card
        </Text>
        <Flex
          column
          width="100%"
        >
          {paymentMethods.map(m => (
            <Flex
              key={m.id}
              width="100%"
              align="center"
              spacing={5}
              clickable
              onClick={() => onSetDefaultPaymentMethod(m)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width={18}
                height={18}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="m9 12 2 2 4-4" />
              </svg>
              <Text
                size="13px"
                color="#8e8e8e"
              >
                ending in
                {' '}
                {m.card.last4}
              </Text>
            </Flex>
          ))}
        </Flex>
        {cardFormIsVisible ? (
          <Flex
            width="100%"
            padding="10px 0"
            column
          >
            <hr />
            <AddCardForm
              submitText="Finish"
              onCancel={() => setCardFormVisible(false)}
              onSuccess={handleAddCard}
            />
            <hr />
          </Flex>
        ) : (
          <S.LinkButton onClick={() => setCardFormVisible(true)}>
            +Add new card
          </S.LinkButton>
        )}
      </Flex>
      {errorMsg && (
        <Flex
          width="100%"
          justify="center"
          margin="15px 0"
        >
          <Text
            color="#b64135"
          >
            {errorMsg}
          </Text>
        </Flex>
      )}
      <Flex
        width="100%"
        justify="center"
        margin="15px 0"
      >
        <Button
          ghost
          appearance="primary"
          isLoading={isLoading}
          isDisabled={isLoading || disabled || paymentMethods.length < 1}
          onClick={() => {
            if (isUpgrade) onUpgrade();
            else if (planId) onSubscribeProduct();
            else onBuy();
          }}
        >
          {buttonText}
        </Button>
      </Flex>
      <Flex width="100%">
        <Text
          size="13px"
          color="#c3c3c3"
        >
          *Upon clicking
          {' '}
          {buttonText}
          , your card will be charged
          and your account will be updated.
        </Text>
      </Flex>
    </>
  );
};

export default Buy;
